import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import InkContactMenu from '../../components/InkContactMenu';
import '../../styles/neevii-home.css';

const HOME_TITLE = 'Neevii Ink — scene-linear image editor for VFX';
const HOME_DESC =
  'Neevii Ink — professional image editing for visual effects. Scene-linear ACES/OCIO colour and generative AI that runs on your own hardware, on Linux and Windows. Built in London.';

/* Background streaks video — Lee's 2026-09-22 DaVinci regrade (same asset /ink uses).
   React does not always render the muted attribute, which blocks autoplay — force it via ref. */
function BgVideo() {
  return (
    <div className="nh-bgvideo" aria-hidden="true">
      <video
        ref={(el) => {
          if (el) {
            el.muted = true;
            el.play().catch(() => {});
          }
        }}
        autoPlay
        loop
        muted
        playsInline
        poster="/assets/videos/Neevii_ink_background_streaks-poster.webp"
      >
        <source src="/assets/videos/Neevii_ink_background_streaks-1920.mp4" type="video/mp4" />
      </video>
    </div>
  );
}

function AccountingShot() {
  return (
    <div className="nh-shot nh-shot-acc">
      <div className="nh-acc-bar">
        <i />
        <i />
        <i />
      </div>
      <div className="nh-acc-grid">
        <div className="nh-tile"><b /><i /></div>
        <div className="nh-tile nh-orange"><b /><i /></div>
        <div className="nh-tile"><b /><i /></div>
        <div className="nh-tile"><b /><i /></div>
        <div className="nh-tile nh-orange"><b /><i /></div>
        <div className="nh-tile"><b /><i /></div>
      </div>
    </div>
  );
}

function AgencyShot() {
  return (
    <div className="nh-shot nh-shot-acc">
      <div className="nh-acc-bar">
        <i />
        <i />
        <i />
      </div>
      <div className="nh-acc-grid">
        <div className="nh-tile"><b /><i /></div>
        <div className="nh-tile nh-orange"><b /><i /></div>
        <div className="nh-tile"><b /><i /></div>
        <div className="nh-tile"><b /><i /></div>
        <div className="nh-tile"><b /><i /></div>
        <div className="nh-tile nh-orange"><b /><i /></div>
      </div>
    </div>
  );
}

const Homepage = () => {
  const rootRef = useRef(null);

  /* Dark canvas behind the page, removed on unmount (see html.nh-root). */
  useEffect(() => {
    document.documentElement.classList.add('nh-root');
    return () => document.documentElement.classList.remove('nh-root');
  }, []);

  /* Scroll reveals: IntersectionObserver + 2.5s safety timer so content never
     strands at opacity 0 (handover motion rule). */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = Array.from(root.querySelectorAll('.nh-rv'));
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((el) => io.observe(el));
    const safety = setTimeout(() => {
      els.forEach((el) => el.classList.add('in'));
    }, 2500);
    return () => {
      io.disconnect();
      clearTimeout(safety);
    };
  }, []);

  return (
    <div className="nh-home" ref={rootRef}>
      <Seo title={HOME_TITLE} description={HOME_DESC} />

      <header className="nh-header">
        <nav className="nh-nav" aria-label="Primary">
          <Link className="nh-logo" to="/">
            <img src="/assets/images/neevii-logo-mark-64.webp" alt="Neevii" width="26" height="26" />
            Neevii
          </Link>
          <span className="nh-sp" />
          <Link className="nh-lk" to="/ink">Neevii Ink</Link>
          <InkContactMenu />
          {/* 2026-09-28: Accounting/Agency nav links hidden on Lee's request (homepage focuses on Neevii Ink)
          <Link className="nh-lk" to="/accounting">Neevii Accounting</Link>
          <Link className="nh-lk" to="/agency">Agency Partnerships</Link>
          */}
        </nav>
      </header>

      <main>
        {/* HERO */}
        <section className="nh-hero">
          <BgVideo />
          <img
            className="nh-brandmark nh-rv"
            src="/assets/images/neevii-logo-mark-256.webp"
            alt="Neevii logo"
            width="104"
            height="104"
          />
          <h1 className="nh-rv">Independent software for creative and professional work.</h1>
          <p className="nh-lead nh-rv">
            Products designed by a working professional, for working professionals.
          </p>
          <div className="nh-meta nh-rv">
            London<i>•</i>Independent<i>•</i>Design-led
          </div>
        </section>

        {/* PRODUCT FAMILY */}
        <section className="nh-family nh-wrap-wide">
          {/* Neevii Ink — flagship */}
          <article className="nh-product nh-p-ink nh-featured nh-rv">
            <div className="nh-media">
              {/* 2026-09-28: Flagship · Free chip hidden on Lee's request — reinstate this span to bring it back
              <span className="nh-flagchip">Flagship · Free</span>
              */}
              <img
                className="nh-shot-img"
                src="/assets/images/ink/ui-home-v01.webp"
                alt="Neevii Ink — image editing & compositing workspace with scene-linear tools and local generative AI"
                width="1964"
                height="1147"
                loading="lazy"
              />
            </div>
            <div className="nh-info">
              <div className="nh-kicker">Image editing &amp; compositing</div>
              <h2 className="nh-ink-title">
                <img src="/assets/images/ink/logo-64.webp" alt="" width="60" height="60" />
                {'Neevii '}
                <em>Ink</em>
              </h2>
              <p className="nh-desc nh-desc-lg">
                Professional image editing for visual effects
              </p>
              <div className="nh-pbtns">
                <Link className="nh-pbtn nh-primary" to="/get-ink#download">↓ Download Free</Link>
                <Link className="nh-pbtn nh-ghost" to="/ink">Explore Neevii Ink</Link>
              </div>
            </div>
          </article>

          {/* Secondary row: Accounting + Agency — HIDDEN 2026-09-28 on Lee's request
              (homepage focuses on Neevii Ink; Lee will re-organise and decide how to
              re-add these later). Reinstate this whole block to bring the duo back.
          <div className="nh-duo">
            <article className="nh-product nh-p-acc nh-half nh-rv">
              <div className="nh-info">
                <div className="nh-kicker">UK accounting software</div>
                <h2>Neevii Accounting</h2>
                <p className="nh-desc">UK accounting software for small business.</p>
                <a className="nh-go" href="/accounting">
                  <span className="nh-arr">→</span> Open Neevii Accounting
                </a>
              </div>
              <div className="nh-media">
                <AccountingShot />
              </div>
            </article>

            <article className="nh-product nh-p-agency nh-half nh-rv">
              <div className="nh-info">
                <div className="nh-kicker">Design-led portals &amp; dashboards</div>
                <h2>Agency Partnerships</h2>
                <p className="nh-desc">
                  Polished portals, dashboards and digital products — including UI/UX demos of our
                  design work.
                </p>
                <Link className="nh-go" to="/agency">
                  <span className="nh-arr">→</span> View Agency Partnerships
                </Link>
              </div>
              <div className="nh-media">
                <AgencyShot />
              </div>
            </article>
          </div>
          End of hidden duo */}
        </section>

        {/* quiet trust strip */}
        <section className="nh-trust">
          <div className="nh-mantra nh-rv">Neevii</div>
          <p className="nh-lead nh-rv" style={{ margin: '16px auto 0', textAlign: 'center' }}>
            Design-led software, built in the open — exploring what independent tools can be.
          </p>
        </section>
      </main>

      <footer className="nh-footer">
        <div className="nh-wrap">
          <div className="nh-fwrap">
            <div className="nh-fbrand">
              <img src="/assets/images/neevii-logo-mark-64.webp" alt="Neevii" height="22" />
              Independent software, London
            </div>
            <div className="nh-fright">
              <div>
                <b>Neevii Ink</b>
                <Link to="/ink">Full tour</Link>
                <Link to="/get-ink">Download</Link>
                <Link to="/get-ink">Licensing</Link>
              </div>
              {/* 2026-10-07: Accounting / Agency / About links hidden on Lee's request
                  while the homepage focuses on Neevii Ink. Reinstate the block to bring them back.
              <div>
                <b>Products</b>
                <a href="https://app.neevii.com">Neevii Accounting</a>
                <Link to="/agency">Agency Partnerships</Link>
              </div>
              <div>
                <b>Company</b>
                <a href="#">About</a>
              </div>
              */}
              <div>
                <b>Company</b>
                <Link to="/privacy">Privacy</Link>
                <Link to="/terms">Terms</Link>
                <a href="mailto:support@neevii.com">Contact</a>
              </div>
              <div>
                <b>Ink support</b>
                <a href="mailto:ink-support@neevii.com">Contact Ink support</a>
                <Link to="/ink/report-bug">Report a bug</Link>
                <Link to="/ink/suggest-feature">Suggest a feature</Link>
              </div>
            </div>
          </div>
          <div className="nh-copyright">
            <span>© {new Date().getFullYear()} Neevii. All rights reserved.</span>
            <span>neevii.com</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
